import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  addProcessedFiles,
  addProcessingFiles,
  loadFiles,
  publishImportedQuestions,
  updateFilename,
} from "./actions";
import { QuestionImportSlice } from "./types";

export const initialState: QuestionImportSlice = {
  loading: false,
  processingFiles: 0,
  processedFiles: 0,
  importedQuestions: [],
  allTags: [],
};

export const questionImportSlice = createSlice({
  name: "questionImport",
  initialState,
  reducers: {
    clear() {
      return initialState;
    },
    loadTags(state, action: PayloadAction<string[]>) {
      state.allTags = action.payload;
    },
    createTag(state, action: PayloadAction<string>) {
      state.allTags.push(action.payload);
    },
    assignTag(
      state,
      action: PayloadAction<{ fileOriginId: string; tag: string }>
    ) {
      const importedQuestion = state.importedQuestions.find(
        (bank) => bank.fileOrigin.fileOriginId === action.payload.fileOriginId
      );

      if (importedQuestion) {
        importedQuestion.tags = importedQuestion.tags ?? [];
        importedQuestion.tags.push(action.payload.tag);
      }
    },
    deleteTag(
      state,
      action: PayloadAction<{ fileOriginId: string; tag: string }>
    ) {
      const importedQuestion = state.importedQuestions.find(
        (row) => row.fileOrigin.fileOriginId === action.payload.fileOriginId
      );

      if (importedQuestion) {
        importedQuestion.tags = importedQuestion.tags?.filter(
          (tag) => tag !== action.payload.tag
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addProcessingFiles, (state, action) => {
      state.processingFiles += action.payload.filesCount;
    });
    builder.addCase(addProcessedFiles, (state, action) => {
      state.processedFiles += action.payload.filesCount;
    });
    builder.addCase(loadFiles.pending, (state, action) => {
      state.loading = true;
      state.processedFiles = 0;
      state.processingFiles = action.meta?.arg?.length ?? 0;
      state.importedQuestions = [];
    });
    builder.addCase(loadFiles.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(loadFiles.fulfilled, (state, action) => {
      state.importedQuestions = action.payload.flat();
      state.loading = false;
    });
    builder.addCase(updateFilename.pending, (state, action) => {
      const importedQuestion = state.importedQuestions.find(
        (bank) => bank.fileOrigin.fileOriginId === action.meta.arg.fileOriginId
      );
      if (importedQuestion) {
        importedQuestion.fileOrigin.filename = action.meta.arg.filename;
        importedQuestion.fileOrigin.loading = true;
      }
    });
    builder.addCase(updateFilename.fulfilled, (state, action) => {
      const importedQuestion = state.importedQuestions.find(
        (q) => q.fileOrigin.fileOriginId === action.meta.arg.fileOriginId
      );
      if (importedQuestion) {
        importedQuestion.fileOrigin.filename = action.meta.arg.filename;
        importedQuestion.fileOrigin.id = action.payload?.id.toString();
        importedQuestion.fileOrigin.loading = false;
      }
    });
    builder.addCase(updateFilename.rejected, (state, action) => {
      const importedQuestion = state.importedQuestions.find(
        (q) => q.fileOrigin.fileOriginId === action.meta.arg.fileOriginId
      );
      if (importedQuestion) {
        importedQuestion.fileOrigin.loading = false;
        importedQuestion.fileOrigin.error = action.payload?.error;
        importedQuestion.fileOrigin.filename = action.payload?.filename;
      }
    });
    builder.addCase(publishImportedQuestions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(publishImportedQuestions.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(publishImportedQuestions.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const { assignTag, createTag, deleteTag, clear, loadTags } =
  questionImportSlice.actions;

export const questionImportReducer = questionImportSlice.reducer;
